// House of Uncommon Grounds section behavior: scroll reveals and the paths photo lens.
(() => {
  const reveal = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        reveal.unobserve(entry.target);
      });
    },
    { threshold: 0.2 }
  );

  const initReveals = (root) => {
    root.querySelectorAll('.hug-reveal, .hug-intro, .hug-tile--quote').forEach((el, i) => {
      if (el.classList.contains('hug-reveal')) el.style.transitionDelay = (i % 4) * 90 + 'ms';
      reveal.observe(el);
    });
  };

  const initLens = (root) => {
    root.querySelectorAll('.hug-lens').forEach((lens) => {
      const paths = [...lens.querySelectorAll('.hug-path')];
      if (!paths.length) return;
      const activate = (path) => paths.forEach((p) => p.classList.toggle('is-active', p === path));
      activate(paths[0]);
      paths.forEach((path) => {
        path.querySelector('.hug-path__txt').addEventListener('pointerenter', () => activate(path));
        path.addEventListener('focusin', () => activate(path));
      });
      // On desktop the photo also follows the path scrolled to the middle of the screen.
      const follow = new IntersectionObserver(
        (entries) => entries.forEach((e) => e.isIntersecting && activate(e.target.closest('.hug-path'))),
        { rootMargin: '-40% 0px -40% 0px' }
      );
      paths.forEach((p) => follow.observe(p.querySelector('.hug-path__txt')));
      // The pinned heading's height offsets the sticky photo below it.
      const section = lens.closest('.hug-paths');
      const head = section && section.querySelector('.hug-paths__head');
      if (head && 'ResizeObserver' in window) {
        new ResizeObserver(() => section.style.setProperty('--hug-paths-head', head.offsetHeight + 'px')).observe(head);
      }
    });
  };

  // Page-hero rotators fade through their photos; reduced motion keeps the first one.
  const initRotators = (root) => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    root.querySelectorAll('[data-hug-rotator]').forEach((box) => {
      const imgs = [...box.querySelectorAll('img')];
      if (imgs.length < 2 || box.dataset.hugRotating) return;
      box.dataset.hugRotating = '1';
      let i = 0;
      const timer = setInterval(() => {
        // The theme editor replaces section DOM; stop timers for boxes that left the page.
        if (!box.isConnected) return clearInterval(timer);
        if (document.hidden) return;
        imgs[i].classList.remove('is-on');
        i = (i + 1) % imgs.length;
        imgs[i].loading = 'eager';
        imgs[i].classList.add('is-on');
      }, 3500);
    });
  };

  // Links like ?interest=Wholesale%20samples#inquiry preselect the inquiry form's interest.
  const initInterest = (root) => {
    const wanted = new URLSearchParams(location.search).get('interest');
    if (!wanted) return;
    root.querySelectorAll('[data-hug-interest]').forEach((select) => {
      const match = [...select.options].find((o) => o.text.trim().toLowerCase() === wanted.toLowerCase());
      if (match) select.value = match.value;
    });
  };

  // Remember utm_ tags from the landing URL for this visit (sessionStorage), so an inquiry sent from a later page
  // still says which tagged link (e.g. the Chamber directory) brought the visitor in.
  const campaignKey = 'hug-campaign';
  const readCampaign = () => {
    const params = new URLSearchParams(location.search);
    const parts = ['utm_source', 'utm_medium', 'utm_campaign'].map((k) => (params.get(k) || '').trim().slice(0, 80));
    const tagged = parts.filter(Boolean).join(' / ');
    try {
      if (tagged) sessionStorage.setItem(campaignKey, tagged);
      return sessionStorage.getItem(campaignKey) || '';
    } catch (e) {
      return tagged;
    }
  };
  const campaign = readCampaign();
  const initCampaign = (root) => {
    if (!campaign) return;
    root.querySelectorAll('[data-hug-campaign]').forEach((input) => {
      input.value = campaign;
      input.disabled = false;
    });
  };

  // Shopify reloads the page after a contact form post; bring the thank-you or error into view.
  const initFormResult = (root) => {
    const result = root.querySelector('[data-hug-form-result]');
    if (!result) return;
    result.scrollIntoView({ block: 'center' });
    result.focus({ preventScroll: true });
  };

  // Appstle Build-A-Box (/apps/subscriptions/bb/...): match the shop's product cards.
  // Roast meter read from the roast tag. The bag photo is already each coffee's first image in Shopify, so no image swap.
  const initBox = () => {
    if (!location.pathname.startsWith('/apps/subscriptions/bb/')) return;
    const levels = {
      'light roast': [1, 'Light roast'],
      'medium-light roast': [2, 'Medium-light roast'],
      'medium light roast': [2, 'Medium-light roast'],
      'medium roast': [3, 'Medium roast'],
      'medium-dark roast': [4, 'Medium-dark roast'],
      'medium dark roast': [4, 'Medium-dark roast'],
      'dark roast': [5, 'Dark roast'],
    };
    const enhance = () => {
      document.querySelectorAll('.as-products > div').forEach((card) => {
        const title = card.querySelector('h3');
        if (!title || card.querySelector('.hug-roast')) return;
        const tags = (card.querySelector('.as-top-tags')?.textContent || '').split(',').map((t) => t.trim().toLowerCase());
        const match = tags.map((t) => levels[t]).find(Boolean);
        if (!match) return;
        const [level, label] = match;
        const meter = document.createElement('div');
        meter.className = 'hug-roast';
        const dots = document.createElement('span');
        dots.className = 'hug-roast__dots';
        dots.setAttribute('aria-hidden', 'true');
        for (let i = 1; i <= 5; i++) {
          const dot = document.createElement('i');
          if (i <= level) dot.className = 'on';
          dots.append(dot);
        }
        const text = document.createElement('span');
        text.className = 'hug-roast__label';
        text.textContent = label;
        meter.append(dots, text);
        title.after(meter);
      });
    };
    // Appstle renders and re-renders cards (search, add to box) at any time; batch reruns to one per frame.
    let queued = false;
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        enhance();
        enhanceBoxLayout();
      });
    };
    enhance();
    initBoxLayout();
    enhanceBoxLayout();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  };

  // Build Your Subscription layout (Ben, September 26): our own header and steps instead of Appstle's title,
  // search and progress bar; first-sentence descriptions with Read more; the cart opens only from a Cart
  // button that sits beside Proceed; checkout with 3 to 6 bags.
  // Box rules, matching Appstle box 4uqexgtu: 3 to 6 bags; 3–5 get the plan's 5%, 6 adds the tier for 10% in total.
  const MIN_BAGS = 3;
  const MAX_BAGS = 6;
  const canCheckout = (count) => count >= MIN_BAGS && count <= MAX_BAGS;
  let cartWantedAt = 0; // when a Cart button was clicked; the drawer may open within a few seconds of it
  let cartOpen = false;
  let descriptions = null;

  const boxCount = () => {
    const text = document.querySelector('.as-progress-text')?.textContent || '';
    const match = text.match(/(\d+)\s*\/\s*\d+/);
    return match ? Number(match[1]) : 0;
  };

  const textLines = (html) => {
    // Rebuild the store's product description as plain text lines, never as injected HTML.
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    doc.querySelectorAll('meta, script, style').forEach((n) => n.remove());
    doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
    return [...doc.querySelectorAll('p, li')]
      .flatMap((el) => el.textContent.split('\n'))
      .map((line) => line.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim())
      .filter(Boolean);
  };

  const loadDescriptions = () => {
    if (descriptions) return descriptions;
    descriptions = fetch('/collections/legacy-blends/products.json?limit=50')
      .then((r) => (r.ok ? r.json() : { products: [] }))
      .then(({ products }) => {
        const map = new Map();
        products.forEach((product) => {
          const [first = '', ...rest] = textLines(product.body_html);
          // The first line repeats the name ("Berkeley's Blend — ..."); keep the words after the dash.
          const teaser = first.replace(/^[^—–-]*[—–-]\s*/, '') || first;
          map.set(product.title.trim().toLowerCase(), { teaser, more: rest });
        });
        return map;
      })
      .catch(() => {
        descriptions = null; // retry on the next render instead of caching a failure
        return new Map();
      });
    return descriptions;
  };

  const initBoxLayout = () => {
    document.documentElement.classList.add('hug-box-page');

    // Clicks on either Cart button are the only way the cart may open.
    document.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('.as-cart-btn, .hug-box-cart')) cartWantedAt = Date.now();
        const proceed = event.target.closest('.as-proceed-to-checkout-btn');
        if (proceed) {
          const count = boxCount();
          // 0 means the count wasn't readable; Appstle's own minimum still applies, so don't block every checkout.
          if (count && !canCheckout(count)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            const note = document.querySelector('.hug-box-bar__note');
            if (note) note.textContent = `Choose ${MIN_BAGS} to ${MAX_BAGS} bags to check out. You have ${count}.`;
          }
        }
      },
      true
    );
  };

  const enhanceBoxLayout = () => {
    const bundle = document.querySelector('.as-bundle-html-wrapper');
    if (!bundle) return;

    if (!document.querySelector('.hug-box-intro')) {
      const intro = document.createElement('section');
      intro.className = 'hug-box-intro';
      intro.innerHTML = `
        <h1>Build Your Subscription</h1>
        <p>Fresh-roasted Legacy Blends, delivered on your schedule. Every bag is 12&nbsp;oz, whole bean or drip.</p>
        <ol class="hug-box-steps">
          <li><strong>Choose 3 to 6 bags.</strong> Order 3 or more bags to save 5%, or 6 bags to save 10%, on every delivery. Mix blends or pick the same one more than once.</li>
          <li><strong>Pick how often.</strong> Open the cart to choose weekly, every two weeks or monthly. Monthly is selected unless you change it.</li>
          <li><strong>Check out.</strong> Select Proceed to checkout, then choose shipping or pickup at the roastery.</li>
        </ol>`;
      bundle.before(intro);
    }

    const wrapper = document.querySelector('.as-checkout-btn-wrapper');
    if (wrapper && !wrapper.querySelector('.hug-box-cart')) {
      const cart = document.createElement('button');
      cart.type = 'button';
      cart.className = 'hug-box-cart';
      cart.addEventListener('click', () => document.querySelector('.as-cart-btn')?.click());
      const note = document.createElement('p');
      note.className = 'hug-box-bar__note';
      note.setAttribute('aria-live', 'polite');
      wrapper.append(cart);
      wrapper.after(note);
    }
    const count = boxCount();
    const cartButton = document.querySelector('.hug-box-cart');
    if (cartButton) {
      const label = `Cart (${count} ${count === 1 ? 'bag' : 'bags'})`;
      if (cartButton.textContent !== label) cartButton.textContent = label;
    }
    const note = document.querySelector('.hug-box-bar__note');
    if (note && canCheckout(count) && note.textContent) note.textContent = '';

    // Sticky count and checkout bar, so shoppers don't scroll to the bottom to check out.
    let sticky = document.querySelector('.hug-box-sticky');
    if (!sticky) {
      sticky = document.createElement('div');
      sticky.className = 'hug-box-sticky';
      sticky.setAttribute('aria-live', 'polite');
      sticky.innerHTML = '<p class="hug-box-sticky__text"></p><div class="hug-box-sticky__btns"><button type="button" class="hug-box-sticky__cart">Cart</button><button type="button" class="hug-box-sticky__go">Checkout</button></div>';
      sticky.querySelector('.hug-box-sticky__cart').addEventListener('click', () => document.querySelector('.hug-box-cart')?.click());
      sticky.querySelector('.hug-box-sticky__go').addEventListener('click', () => {
        [...document.querySelectorAll('.as-checkout-btn-wrapper .as-proceed-to-checkout-btn')].find((b) => b.offsetParent)?.click();
      });
      document.body.append(sticky);
      sticky.watcher = new IntersectionObserver(([entry]) => sticky.classList.toggle('is-covered', entry.isIntersecting));
    }
    // Hide while the page's own Proceed and Cart buttons are on screen (re-watch if Appstle replaces them).
    const bottom = document.querySelector('.as-checkout-btn-wrapper');
    if (bottom && sticky.watched !== bottom) {
      sticky.watcher.disconnect();
      sticky.watcher.observe(bottom);
      sticky.watched = bottom;
    }
    const message =
      count === 0 ? '' :
      count < 3 ? `${count} of 3 bags. Add ${3 - count} more to check out.` :
      count < 6 ? `${count} bags, 5% off. Add ${6 - count} more for 10% off.` :
      '6 bags, 10% off every delivery.';
    const stickyText = sticky.querySelector('.hug-box-sticky__text');
    if (stickyText.textContent !== message) stickyText.textContent = message;
    sticky.classList.toggle('is-on', count > 0);
    sticky.querySelector('.hug-box-sticky__go').disabled = !canCheckout(count);

    // Keep Appstle's cart drawer shut unless a Cart button asked for it.
    const dialog = document.querySelector('.as-bundle-cart-dailog');
    if (dialog) {
      if (cartOpen || Date.now() - cartWantedAt < 4000) {
        cartOpen = true;
        // On <html>, because Appstle rewrites the drawer's own class list as it animates.
        document.documentElement.classList.add('hug-cart-allowed');
      } else if (!dialog.dataset.hugClosing) {
        dialog.dataset.hugClosing = 'true';
        dialog.querySelector('.as-close-btn')?.click();
      }
    } else if (cartOpen) {
      cartOpen = false;
      cartWantedAt = 0;
      document.documentElement.classList.remove('hug-cart-allowed');
    }

    // First sentence of each description, the rest behind Read more.
    loadDescriptions().then((map) => {
      document.querySelectorAll('.as-products > div').forEach((card) => {
        if (card.querySelector('.hug-desc')) return;
        const title = card.querySelector('h3')?.textContent.trim().toLowerCase();
        const entry = title && map.get(title);
        const original = card.querySelector('p.as-text-gray-500');
        if (!entry || !original) return;
        const desc = document.createElement('div');
        desc.className = 'hug-desc';
        const teaser = document.createElement('p');
        teaser.textContent = entry.teaser;
        desc.append(teaser);
        if (entry.more.length) {
          const details = document.createElement('details');
          const summary = document.createElement('summary');
          summary.textContent = 'Read more';
          details.append(summary);
          // Fixed order: story paragraphs, then a short fact list from "Label: value" lines, then any closing line.
          // Roast Level is skipped because the roast meter above already shows it.
          const story = [];
          const closing = [];
          const facts = document.createElement('dl');
          facts.className = 'hug-desc__facts';
          entry.more.forEach((line) => {
            const fact = line.match(/^([A-Za-z][A-Za-z ]{1,24}):\s*(.+)$/);
            if (fact && /^roast level$/i.test(fact[1].trim())) return;
            if (!fact) {
              (facts.childElementCount ? closing : story).push(line);
              return;
            }
            const dt = document.createElement('dt');
            dt.textContent = fact[1].trim();
            const dd = document.createElement('dd');
            const parts = fact[2].split('·').map((s) => s.trim()).filter(Boolean);
            if (/notes/i.test(fact[1]) && parts.length > 1) {
              parts.forEach((note) => {
                const tag = document.createElement('span');
                tag.className = 'hug-desc__note';
                tag.textContent = note;
                dd.append(tag);
              });
            } else {
              dd.textContent = fact[2].trim();
            }
            facts.append(dt, dd);
          });
          const para = (text, className) => {
            const el = document.createElement('p');
            el.textContent = text;
            if (className) el.className = className;
            return el;
          };
          story.forEach((line) => details.append(para(line)));
          if (facts.childElementCount) details.append(facts);
          closing.forEach((line) => details.append(para(line, 'hug-desc__footnote')));
          // Keep the card's own click handler (Appstle opens a product popup) from firing.
          details.addEventListener('click', (event) => event.stopPropagation());
          desc.append(details);
        }
        original.after(desc);
      });
    });
  };

  // Desktop header dropdowns open on hover. Click and keyboard keep Dawn's own toggle.
  const initHoverMenus = () => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    document.querySelectorAll('header-menu details').forEach((details) => {
      const summary = details.querySelector('summary');
      let closeTimer;
      const set = (open) => {
        details.open = open;
        summary.setAttribute('aria-expanded', String(open));
      };
      details.addEventListener('mouseenter', () => {
        clearTimeout(closeTimer);
        if (details.open) return;
        details.dataset.hugHover = 'true';
        set(true);
      });
      details.addEventListener('mouseleave', () => {
        if (!details.dataset.hugHover) return;
        // A short delay so the pointer can cross from the menu label to the dropdown.
        closeTimer = setTimeout(() => {
          delete details.dataset.hugHover;
          set(false);
        }, 150);
      });
      // A click while hover has it open would otherwise toggle it shut under the pointer.
      summary.addEventListener('click', (event) => {
        if (!details.dataset.hugHover) return;
        event.preventDefault();
        delete details.dataset.hugHover;
      });
    });
  };

  const init = (root = document) => {
    initReveals(root);
    initLens(root);
    initRotators(root);
    initInterest(root);
    initCampaign(root);
    initFormResult(root);
  };

  init();
  initBox();
  initHoverMenus();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
